import { useCallback, useEffect, useRef, useState } from "react"
import { message, notification } from "antd"
import useOpus from "contexts/opus/hooks/use-opus"

const LOADING_MESSAGE_KEY = "download-document-pdf"

type DownloadDocumentPdfArgs =
  | { entityType: "quote"; entityId: string }
  | { entityType: "policy"; entityId: string }

export const useDownloadDocumentPdf = ({
  entityType,
  entityId
}: DownloadDocumentPdfArgs): IUseDownloadDocumentPdf => {
  const [isDownloading, setIsDownloading] = useState(false)
  const isDownloadingRef = useRef(false)
  const isMounted = useRef(true)
  const { quoteClient, policyClient } = useOpus()

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
      message.destroy(LOADING_MESSAGE_KEY)
    }
  }, [])

  const downloadDocumentPdf = useCallback(
    async (hidePremiumBreakdown = false) => {
      if (isDownloadingRef.current || !entityId) return

      isDownloadingRef.current = true
      setIsDownloading(true)
      message.loading({
        content: "Generating document PDF…",
        key: LOADING_MESSAGE_KEY,
        duration: 0
      })

      try {
        const response =
          entityType === "quote"
            ? await quoteClient.downloadDocumentPdf(
                entityId,
                hidePremiumBreakdown
              )
            : await policyClient.downloadDocumentPdf(
                entityId,
                hidePremiumBreakdown
              )

        const a = document.createElement("a")
        a.style.display = "none"
        document.body.appendChild(a)

        const url = window.URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" })
        )
        a.href = url
        a.download = response.headers["x-filename"] || "document.pdf"
        a.click()
        window.URL.revokeObjectURL(url)
        a.remove()

        message.destroy(LOADING_MESSAGE_KEY)
        notification.success({
          message:
            "Document downloaded successfully. Please check your downloads folder."
        })
      } catch {
        message.destroy(LOADING_MESSAGE_KEY)
        notification.error({
          message:
            "An error occurred when attempting to download the document. Please try again, or contact support."
        })
      } finally {
        isDownloadingRef.current = false
        message.destroy(LOADING_MESSAGE_KEY)
        if (isMounted.current) {
          setIsDownloading(false)
        }
      }
    },
    [entityId, entityType, policyClient, quoteClient]
  )

  return { downloadDocumentPdf, isDownloading }
}

interface IUseDownloadDocumentPdf {
  downloadDocumentPdf: (hidePremiumBreakdown?: boolean) => Promise<void>
  isDownloading: boolean
}
